// @ts-nocheck
// ============================================================================
// RtUpsellProduct Custom Element
// ============================================================================

/**
 * Upsell Product Custom Element
 * Renders a single product card using the template or programmatically.
 *
 * @fires rt-upsell-product:add-to-cart - When product is added to cart
 */
class RtUpsellProduct extends HTMLElement {
  static tagName = 'rt-upsell-product';

  /** @type {object | null} */
  _product = null;

  constructor() {
    super();
  }

  connectedCallback() {
    // If product is already set, render it
    if (this._product) {
      this._render();
    }
  }

  /**
   * Set the product data and render
   * @param {object} product - The product object from Shopify API
   */
  setProduct(product) {
    this._product = product;
    if (this.isConnected) {
      this._render();
    }
  }

  /**
   * Get the current product data
   * @returns {object | null}
   */
  getProduct() {
    return this._product;
  }

  /**
   * Render the product using the template
   */
  _render() {
    if (!this._product) {
      return;
    }

    const template = document.getElementById('rt-upsell-product');
    if (!template) {
      // Fallback to programmatic rendering
      this._renderProgrammatic();
      return;
    }

    // Clone the template content
    const clone = template.content.cloneNode(true);
    const container = clone.querySelector('rt-upsell-product');

    if (container) {
      // Replace placeholders in the template
      this._processTemplate(container);

      // Copy the inner content
      this.innerHTML = container.innerHTML;
      this.className = container.className;
    } else {
      this._renderProgrammatic();
    }

    this._setupEventListeners();
  }

  /**
   * Process the template and replace placeholders
   * @param {Element} container
   */
  _processTemplate(container) {
    const product = this._product;
    const variant = product.variants?.[0];
    const hasComparePrice = product.compare_at_price > product.price;

    // Replace text placeholders
    container.innerHTML = this._replaceTemplatePlaceholders(
      container.innerHTML,
      product,
      hasComparePrice
    );

    // Process template slots
    this._processImageSlot(container, product);
    this._processQuickAddSlot(container, product, variant);
    this._processComparePriceSlot(container, hasComparePrice);
  }

  /**
   * Replace template placeholders with product data
   * @param {string} html
   * @param {object} product
   * @param {boolean} hasComparePrice
   * @returns {string}
   */
  _replaceTemplatePlaceholders(html, product, hasComparePrice) {
    return html
      .replace(/\[\[product_link\]\]/g, product.url)
      .replace(/\[\[product_title\]\]/g, this._escapeHtml(product.title))
      .replace(/\[\[product_price\]\]/g, this._formatMoney(product.price))
      .replace(
        /\[\[product_compare_at_price\]\]/g,
        hasComparePrice ? this._formatMoney(product.compare_at_price) : ''
      )
      .replace(/\[\[price_class\]\]/g, hasComparePrice ? 'rt-upsell-product-price--on-sale' : '');
  }

  /**
   * Process the image slot in the template
   * @param {Element} container
   * @param {object} product
   */
  _processImageSlot(container, product) {
    const imageSlot = container.querySelector('slot[name="image"]');
    if (imageSlot && product.featured_image) {
      const fragment = document.createDocumentFragment();

      // Primary image
      const primaryImg = document.createElement('img');
      primaryImg.src = this._getImageUrl(product.featured_image, '400x');
      primaryImg.alt = product.title;
      primaryImg.className = 'rt-upsell-product-image rt-upsell-product-image--primary';
      primaryImg.loading = 'lazy';
      primaryImg.width = 400;
      primaryImg.height = 400;
      fragment.appendChild(primaryImg);

      // Secondary image (if available)
      const secondaryImageSrc = this._getSecondaryImage(product);
      if (secondaryImageSrc) {
        const secondaryImg = document.createElement('img');
        secondaryImg.src = this._getImageUrl(secondaryImageSrc, '400x');
        secondaryImg.alt = product.title;
        secondaryImg.className = 'rt-upsell-product-image rt-upsell-product-image--secondary';
        secondaryImg.loading = 'lazy';
        secondaryImg.width = 400;
        secondaryImg.height = 400;
        fragment.appendChild(secondaryImg);
      }

      imageSlot.replaceWith(fragment);
    }
  }

  /**
   * Process the quick add button slot in the template
   * @param {Element} container
   * @param {object} product
   * @param {object} variant
   */
  _processQuickAddSlot(container, product, variant) {
    const quickAddSlot = container.querySelector('slot[name="quick_add_button"]');
    if (quickAddSlot && product.available) {
      const quickAddHtml = this._createQuickAddButton(product, variant);
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = quickAddHtml;
      quickAddSlot.replaceWith(tempDiv.firstElementChild || '');
    } else if (quickAddSlot) {
      quickAddSlot.remove();
    }
  }

  /**
   * Process the compare price slot in the template
   * @param {Element} container
   * @param {boolean} hasComparePrice
   */
  _processComparePriceSlot(container, hasComparePrice) {
    if (!hasComparePrice) {
      const comparePriceSlot = container.querySelector('slot[name="compare_at_price"]');
      if (comparePriceSlot) {
        comparePriceSlot.remove();
      }
    }
  }

  /**
   * Render the product programmatically (fallback)
   */
  // eslint-disable-next-line max-lines-per-function
  _renderProgrammatic() {
    const product = this._product;
    const variant = product.variants?.[0];
    const hasComparePrice = product.compare_at_price > product.price;
    const secondaryImageSrc = this._getSecondaryImage(product);

    this.className = 'rt-upsell-product';
    this.innerHTML = `
      <div class="rt-upsell-product-image-wrapper">
        <a
          href="${product.url}"
          class="rt-upsell-product-image-link"
          data-rt-event="pdp_upsell: rt_pdp_upsell_click"
          data-rt-product-id="${product.id}"
          data-rt-product-handle="${product.handle}"
        >
          ${
            product.featured_image
              ? `<img
              src="${this._getImageUrl(product.featured_image, '400x')}"
              alt="${this._escapeHtml(product.title)}"
              class="rt-upsell-product-image rt-upsell-product-image--primary"
              loading="lazy"
              width="400"
              height="400"
            >${
              secondaryImageSrc
                ? `<img
              src="${this._getImageUrl(secondaryImageSrc, '400x')}"
              alt="${this._escapeHtml(product.title)}"
              class="rt-upsell-product-image rt-upsell-product-image--secondary"
              loading="lazy"
              width="400"
              height="400"
            >`
                : ''
            }`
              : '<div class="rt-upsell-product-no-image">No Image Available</div>'
          }
        </a>
        ${product.available ? this._createQuickAddButton(product, variant) : ''}
      </div>
      <div class="rt-upsell-product-details">
        <a
          href="${product.url}"
          class="rt-upsell-product-title"
          data-rt-event="pdp_upsell: rt_pdp_upsell_click"
          data-rt-product-id="${product.id}"
          data-rt-product-handle="${product.handle}"
        >
          ${this._escapeHtml(product.title)}
        </a>
        <div class="rt-upsell-product-price-wrapper">
          <span class="rt-upsell-product-price${hasComparePrice ? ' rt-upsell-product-price--on-sale' : ''}">
            ${this._formatMoney(product.price)}
          </span>
          ${
            hasComparePrice
              ? `<span class="rt-upsell-product-compare-price">${this._formatMoney(product.compare_at_price)}</span>`
              : ''
          }
        </div>
      </div>
    `;

    this._setupEventListeners();
  }

  /**
   * Create the quick add button HTML
   * @param {object} product
   * @param {object} variant
   * @returns {string}
   */
  _createQuickAddButton(product, variant) {
    const hasMultipleVariants = product.variants?.length > 1;

    if (!hasMultipleVariants && variant) {
      // Simple add to cart button using data-rt attributes
      return `
        <button
          type="button"
          class="rt-cus-upsell-quick-add-btn"
          data-rt-add-to-cart
          data-rt-event="pdp_upsell: rt_pdp_upsell_add_to_cart"
          data-rt-variant-id="${variant.id}"
          data-rt-variant="${variant.id}"
          data-rt-product-id="${product.id}"
          data-rt-product-handle="${product.handle}"
          data-product-id="${product.id}"
          aria-label="Add ${this._escapeHtml(product.title)} to cart"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="19" height="22" viewBox="0 0 19 22" fill="none">
            <path d="M18.0594 18.3251L17.0814 8.16225C16.9243 6.55125 15.5848 5.33625 13.9655 5.33625H13.8834V4.85025C13.8834 2.17575 11.7077 0 9.03317 0C6.36467 0 4.19379 2.17575 4.19379 4.85025V5.33625H4.11167C2.49317 5.33625 1.15329 6.55125 0.995792 8.163L0.0185425 18.3214C-0.0770825 19.2188 0.198543 20.0513 0.813168 20.7262C1.41429 21.399 2.21304 21.7549 3.12317 21.7549H14.9544C15.8649 21.7549 16.6637 21.399 17.2618 20.7289C17.8794 20.0513 18.1547 19.2188 18.0594 18.3251ZM4.94417 4.85063C4.94417 2.58975 6.77867 0.750375 9.03354 0.750375C11.294 0.750375 13.1338 2.58975 13.1338 4.85063V5.33663H4.94417V4.85063ZM16.7049 20.2267C16.2504 20.7356 15.6452 21.0049 14.9544 21.0049H3.12317C2.43242 21.0049 1.82717 20.7356 1.37004 20.2238C0.894543 19.7017 0.690543 19.0883 0.764418 18.3968L1.74204 8.235C1.86204 7.00988 2.88054 6.08625 4.11129 6.08625H4.19342V8.57063C4.19342 8.77763 4.36142 8.94563 4.56842 8.94563C4.77542 8.94563 4.94342 8.77763 4.94342 8.57063V6.08625H13.133V8.57063C13.133 8.77763 13.3007 8.94563 13.508 8.94563C13.7154 8.94563 13.883 8.77763 13.883 8.57063V6.08625H13.9652C15.1963 6.08625 16.2148 7.00988 16.3344 8.23463L17.3135 18.4009C17.3867 19.0882 17.1827 19.7017 16.7049 20.2267Z" fill="black"/>
            <path d="M11.6593 13.1708H9.40851V10.9309C9.40851 10.7239 9.24051 10.5559 9.03351 10.5559C8.82651 10.5559 8.65851 10.7239 8.65851 10.9309V13.1708H6.41864C6.21164 13.1708 6.04364 13.3388 6.04364 13.5458C6.04364 13.7528 6.21164 13.9208 6.41864 13.9208H8.65851V16.1607C8.65851 16.3677 8.82651 16.5357 9.03351 16.5357C9.24051 16.5357 9.40851 16.3677 9.40851 16.1607V13.9208H11.6593C11.8666 13.9208 12.0343 13.7528 12.0343 13.5458C12.0343 13.3388 11.8666 13.1708 11.6593 13.1708Z" fill="black"/>
          </svg>
        </button>
      `;
    } else {
      // Quick buy modal trigger for products with multiple variants
      const quickBuyId = `upsell-quick-buy-${product.id}`;
      return `
        <button
          type="button"
          aria-controls="${quickBuyId}"
          class="rt-cus-upsell-quick-add-btn"
          aria-label="Choose options for ${this._escapeHtml(product.title)}"
          data-quick-buy-trigger
          data-rt-event="pdp_upsell: rt_pdp_upsell_click"
          data-rt-product-id="${product.id}"
          data-rt-product-handle="${product.handle}"
          data-product-handle="${product.handle}"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="19" height="22" viewBox="0 0 19 22" fill="none">
            <path d="M18.0594 18.3251L17.0814 8.16225C16.9243 6.55125 15.5848 5.33625 13.9655 5.33625H13.8834V4.85025C13.8834 2.17575 11.7077 0 9.03317 0C6.36467 0 4.19379 2.17575 4.19379 4.85025V5.33625H4.11167C2.49317 5.33625 1.15329 6.55125 0.995792 8.163L0.0185425 18.3214C-0.0770825 19.2188 0.198543 20.0513 0.813168 20.7262C1.41429 21.399 2.21304 21.7549 3.12317 21.7549H14.9544C15.8649 21.7549 16.6637 21.399 17.2618 20.7289C17.8794 20.0513 18.1547 19.2188 18.0594 18.3251ZM4.94417 4.85063C4.94417 2.58975 6.77867 0.750375 9.03354 0.750375C11.294 0.750375 13.1338 2.58975 13.1338 4.85063V5.33663H4.94417V4.85063ZM16.7049 20.2267C16.2504 20.7356 15.6452 21.0049 14.9544 21.0049H3.12317C2.43242 21.0049 1.82717 20.7356 1.37004 20.2238C0.894543 19.7017 0.690543 19.0883 0.764418 18.3968L1.74204 8.235C1.86204 7.00988 2.88054 6.08625 4.11129 6.08625H4.19342V8.57063C4.19342 8.77763 4.36142 8.94563 4.56842 8.94563C4.77542 8.94563 4.94342 8.77763 4.94342 8.57063V6.08625H13.133V8.57063C13.133 8.77763 13.3007 8.94563 13.508 8.94563C13.7154 8.94563 13.883 8.77763 13.883 8.57063V6.08625H13.9652C15.1963 6.08625 16.2148 7.00988 16.3344 8.23463L17.3135 18.4009C17.3867 19.0882 17.1827 19.7017 16.7049 20.2267Z" fill="black"/>
            <path d="M11.6593 13.1708H9.40851V10.9309C9.40851 10.7239 9.24051 10.5559 9.03351 10.5559C8.82651 10.5559 8.65851 10.7239 8.65851 10.9309V13.1708H6.41864C6.21164 13.1708 6.04364 13.3388 6.04364 13.5458C6.04364 13.7528 6.21164 13.9208 6.41864 13.9208H8.65851V16.1607C8.65851 16.3677 8.82651 16.5357 9.03351 16.5357C9.24051 16.5357 9.40851 16.3677 9.40851 16.1607V13.9208H11.6593C11.8666 13.9208 12.0343 13.7528 12.0343 13.5458C12.0343 13.3388 11.8666 13.1708 11.6593 13.1708Z" fill="black"/>
          </svg>
        </button>
        <quick-buy-modal
          aria-labelledby="quick-buy-modal-title-${quickBuyId}"
          handle="${product.handle}"
          class="quick-buy-modal modal"
          id="${quickBuyId}"
          role="dialog"
          aria-modal="true"
        ></quick-buy-modal>
      `;
    }
  }

  /**
   * Setup event listeners for quick add functionality
   */
  _setupEventListeners() {
    // Handle quick buy modal trigger for multi-variant products
    const quickBuyTrigger = this.querySelector('[data-quick-buy-trigger]');
    if (quickBuyTrigger) {
      quickBuyTrigger.addEventListener('click', this._handleQuickBuyClick.bind(this));
    }

    // Ensure product links (from template rendering) have click tracking attributes
    if (this._product) {
      const productId = this._product.id;
      const productHandle = this._product.handle;

      this.querySelectorAll('a[href]').forEach((link) => {
        if (!link.dataset.rtEvent) {
          link.dataset.rtEvent = 'pdp_upsell: rt_pdp_upsell_click';
          link.dataset.rtProductId = productId;
          link.dataset.rtProductHandle = productHandle;
        }
      });
    }
  }

  /**
   * Handle quick buy button click
   * @param {Event} event
   */
  _handleQuickBuyClick(event) {
    const button = event.currentTarget;
    const modalId = button.getAttribute('aria-controls');
    const modal = this.querySelector(`#${modalId}`);

    if (modal && typeof modal.show === 'function') {
      modal.show();
    }
  }

  /**
   * Get the secondary image for a product
   * @param {object} product - The product object
   * @returns {string | null} - Secondary image URL or null
   */
  _getSecondaryImage(product) {
    // Check if product has multiple images
    if (product.images && product.images.length > 1) {
      return product.images[1];
    }
    // Check media array (alternative structure)
    if (product.media && product.media.length > 1) {
      const secondMedia = product.media[1];
      return secondMedia?.src || secondMedia?.preview_image?.src || null;
    }
    return null;
  }

  /**
   * Get the image URL with size
   * @param {string} src - Original image URL
   * @param {string} size - Size string (e.g., '400x')
   * @returns {string}
   */
  _getImageUrl(src, size) {
    if (!src) {
      return '';
    }

    // Handle Shopify CDN URLs
    if (src.includes('cdn.shopify.com')) {
      return src.replace(/(\.[^.]+)$/, `_${size}$1`);
    }

    return src;
  }

  /**
   * Format money value
   * @param {number} cents - Price in cents
   * @returns {string}
   */
  _formatMoney(cents) {
    if (!cents && cents !== 0) {
      return '';
    }

    const amount = cents / 100;
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'USD',
    });

    return formatter.format(amount);
  }

  /**
   * Escape HTML special characters
   * @param {string} str
   * @returns {string}
   */
  _escapeHtml(str) {
    if (!str) {
      return '';
    }
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }
}

if (!customElements.get(RtUpsellProduct.tagName)) {
  customElements.define(RtUpsellProduct.tagName, RtUpsellProduct);
}

export default RtUpsellProduct;
